/**
 * AcousticWeighting — IEC 61672-1 frequency weighting (A / C / Z) and the
 * standard's exponential time weighting (Fast / Slow / Impulse) for Web Audio.
 *
 * Why this exists
 * ---------------
 * Every sound-level tool here used to hand-roll its weighting out of a few
 * BiquadFilterNodes. Two traps make that reliably wrong, and both were live in
 * /sound-meter until 2026-08-09:
 *
 *   1. `BiquadFilterNode.Q` is read as DECIBELS for `lowpass`/`highpass`
 *      (W3C Web Audio, BiquadFilterNode: alpha = sin(w0) / (2 * 10^(Q/20))).
 *      Writing `Q.value = 0.5` intending a critically-damped section actually
 *      asks for Q = 10^(0.5/20) = 1.059 — a resonant one. Measured in Chromium:
 *      a highpass at 1 kHz with Q.value = 0.5 has +0.50 dB of gain at f0, not
 *      the -6.02 dB a linear Q = 0.5 would give.
 *   2. A-weighting needs SIX poles (20.6 Hz x2, 107.7, 737.9, 12194 Hz x2) and
 *      +2.0 dB of normalisation so 1 kHz lands on exactly 0 dB. Cascades that
 *      drop the 737.9 Hz pole under-attenuate the low end catastrophically:
 *      the old chain was +18.8 dB at 31.5 Hz, +17.6 at 63, +16.7 at 125.
 *      That is precisely the band that breath-on-the-mic, wind and HVAC rumble
 *      live in — which is what A-weighting is *for*.
 *
 * So the design is done once, here, from the standard's pole/zero definition,
 * bilinear-transformed at the context's real sample rate, and shipped as a
 * single IIRFilterNode. Measured against the analytic IEC curve in Chromium:
 * error <= 0.04 dB from 31.5 Hz to 4 kHz, within class 1 tolerance to 16 kHz.
 *
 * Time weighting is separate and equally mandatory: reading the RMS of an
 * analyser's 2048-sample buffer is a ~43 ms rectangular window, which is
 * neither Fast (125 ms) nor Slow (1 s) and reads several dB high on anything
 * modulated. `smoothingTimeConstant` does NOT help — measured: it changes
 * getFloatTimeDomainData by exactly 0.00 dB (it only touches the FFT data).
 *
 * Public API
 * ----------
 *   AcousticWeighting.design(kind, sampleRate) -> {b, a}
 *      Raw IIR coefficients. kind: 'a' | 'c' | 'z'. Memoised.
 *
 *   AcousticWeighting.createNode(ctx, kind) -> {input, output}
 *      A ready graph node pair. Uses IIRFilterNode where available and falls
 *      back to a normalised biquad cascade (worst case 3.0 dB at 16 kHz,
 *      <= 0.03 dB below 500 Hz) on engines without createIIRFilter.
 *
 *   AcousticWeighting.createDetector(mode) -> detector
 *      IEC exponential detector. `.push(meanSquare, dtSeconds)` then `.value()`
 *      returns the time-weighted mean square. `.reset()`, `.setMode(mode)`.
 *      mode: 'fast' (125 ms) | 'slow' (1 s) | 'impulse' (35 ms).
 *
 *   AcousticWeighting.TAU -> {fast: 0.125, slow: 1.0, impulse: 0.035}
 *
 * Notes
 * -----
 * Z-weighting here is a 2-pole 10 Hz highpass only: it exists to strip the DC
 * offset and subsonic rumble that every MEMS mic carries (a DC term inflates
 * RMS directly). The top of the Z band is already bounded by the converter's
 * Nyquist, so no lowpass is added — at 44.1 kHz one would sit uncomfortably
 * close to Nyquist and its warping would do more harm than the standard's
 * generous high-end tolerance costs.
 */
(function () {
    'use strict';

    // IEC 61672-1 pole frequencies (Hz).
    var F1 = 20.598997, F2 = 107.65265, F3 = 737.86223, F4 = 12194.217;

    var POLES = {
        a: [F1, F1, F2, F3, F4, F4],
        c: [F1, F1, F4, F4],
        z: [10, 10]
    };
    var ZEROS_AT_ORIGIN = { a: 4, c: 2, z: 2 };

    var cache = Object.create(null);

    function polyMul(p, q) {
        var r = new Array(p.length + q.length - 1);
        for (var i = 0; i < r.length; i++) r[i] = 0;
        for (var j = 0; j < p.length; j++)
            for (var k = 0; k < q.length; k++) r[j + k] += p[j] * q[k];
        return r;
    }

    // |P(e^{-jw})| for a coefficient array in powers of z^-1.
    function polyMag(p, w) {
        var re = 0, im = 0;
        for (var i = 0; i < p.length; i++) { re += p[i] * Math.cos(-i * w); im += p[i] * Math.sin(-i * w); }
        return Math.sqrt(re * re + im * im);
    }

    /**
     * Bilinear transform of the standard's analog prototype
     *   H(s) = K * s^n / prod(s + w_i)
     * at `sampleRate`, normalised to unity gain at 1 kHz.
     */
    function design(kind, sampleRate) {
        kind = String(kind || 'a').toLowerCase();
        if (!POLES[kind]) kind = 'a';
        var key = kind + '@' + sampleRate;
        if (cache[key]) return cache[key];

        var c = 2 * sampleRate, TWO_PI = 2 * Math.PI;
        var poles = POLES[kind], nz = ZEROS_AT_ORIGIN[kind];

        // numerator: s^nz -> (c(1 - z^-1))^nz, padded with (1 + z^-1) up to the
        // filter order so numerator and denominator have matching length.
        var num = [1], i;
        for (i = 0; i < nz; i++) num = polyMul(num, [c, -c]);
        for (i = 0; i < poles.length - nz; i++) num = polyMul(num, [1, 1]);

        // denominator: prod( c(1 - z^-1) + w_i(1 + z^-1) )
        var den = [1];
        for (i = 0; i < poles.length; i++) {
            var w = TWO_PI * poles[i];
            den = polyMul(den, [c + w, w - c]);
        }

        var a0 = den[0];
        num = num.map(function (v) { return v / a0; });
        den = den.map(function (v) { return v / a0; });

        var w1k = TWO_PI * 1000 / sampleRate;
        var g = polyMag(num, w1k) / polyMag(den, w1k);
        if (g > 0 && isFinite(g)) num = num.map(function (v) { return v / g; });

        var out = { b: num, a: den };
        cache[key] = out;
        return out;
    }

    // Web Audio wants the Q of lowpass/highpass in dB, not linear. See header.
    function qDb(qLinear) { return 20 * Math.log10(qLinear); }

    /**
     * Fallback for engines without createIIRFilter: a biquad cascade built from
     * the same poles, with a GainNode restoring 0 dB at 1 kHz. Each section is
     * prewarped at its own f0 (RBJ), so it is accurate at the low end where it
     * matters and drifts only near Nyquist.
     */
    function cascade(ctx, kind) {
        var nodes = [];
        if (kind === 'a' || kind === 'c') {
            var hp = ctx.createBiquadFilter();
            hp.type = 'highpass'; hp.frequency.value = F1; hp.Q.value = qDb(0.5); // (s+w1)^2
            nodes.push(hp);
            if (kind === 'a') {
                // one biquad carrying the two real poles at 107.7 and 737.9
                var f0 = Math.sqrt(F2 * F3);
                var hp2 = ctx.createBiquadFilter();
                hp2.type = 'highpass'; hp2.frequency.value = f0; hp2.Q.value = qDb(f0 / (F2 + F3));
                nodes.push(hp2);
            }
            var lp = ctx.createBiquadFilter();
            lp.type = 'lowpass'; lp.frequency.value = F4; lp.Q.value = qDb(0.5); // (s+w4)^2
            nodes.push(lp);
        } else {
            var hpz = ctx.createBiquadFilter();
            hpz.type = 'highpass'; hpz.frequency.value = 10; hpz.Q.value = qDb(0.5);
            nodes.push(hpz);
        }

        // Measure the cascade's own 1 kHz gain with the engine's own maths and
        // cancel it, so the caller always gets 0 dB at 1 kHz.
        var probe = new Float32Array([1000]), mag = new Float32Array(1), ph = new Float32Array(1);
        var totalDb = 0;
        for (var i = 0; i < nodes.length; i++) {
            nodes[i].getFrequencyResponse(probe, mag, ph);
            totalDb += 20 * Math.log10(mag[0]);
        }
        var trim = ctx.createGain();
        trim.gain.value = Math.pow(10, -totalDb / 20);
        for (var j = 0; j < nodes.length - 1; j++) nodes[j].connect(nodes[j + 1]);
        nodes[nodes.length - 1].connect(trim);
        return { input: nodes[0], output: trim };
    }

    function createNode(ctx, kind) {
        kind = String(kind || 'a').toLowerCase();
        if (!POLES[kind]) kind = 'a';
        if (typeof ctx.createIIRFilter === 'function') {
            try {
                var d = design(kind, ctx.sampleRate);
                var n = ctx.createIIRFilter(d.b, d.a);
                return { input: n, output: n };
            } catch (e) { /* fall through to the biquad cascade */ }
        }
        return cascade(ctx, kind);
    }

    /**
     * dB SPL that corresponds to digital full scale (1.0 RMS) on a typical
     * consumer device, i.e. the constant in `dB SPL = 10*log10(meanSquare) + X`.
     *
     * Derivation, not a guess: the industry spec point for MEMS microphones is
     * 94 dB SPL (= 1 Pa) at about -26 dBFS, which puts 0 dBFS at ~120 dB SPL.
     * Anything near 94 would mean the microphone clips at conversation level,
     * which every phone falsifies daily.
     *
     * Confirmed against 540 real measurements from /hearing-test's ambient-noise
     * check (telemetry, 2026-07-10..08-09, mixed Chrome/Yandex/Edge/Safari,
     * desktop and mobile), which uses this same anchor:
     *   p10 31 · p25 39 · median 49 · p75 58 · p90 67 dBA
     * That is exactly the distribution of ordinary indoor ambient noise, and the
     * p10 of 31 dBA lands on the self-noise floor of consumer microphones — the
     * quietest thing such a device can physically report. With an anchor of 94
     * the same data would read median 23 dBA and p10 5 dBA: quieter than a
     * recording studio, and 25 dB below the mic's own noise. So 94 is not merely
     * a worse guess, it is excluded by the data. (Those readings were taken with
     * hearing-test's older weighting chain, which ran 0..2 dB high on room
     * spectra; re-deriving from fresh data would move the anchor by about a
     * decibel, nothing like the 26 dB the conclusion rests on.)
     *
     * A third confirmation, found the same day: /projector-noise-meter's own
     * reference table says "30-40 dB — library" and its meter bar is scaled
     * 20-80 dB, yet with a local +94 anchor a normal 25-40 dBA projector landed
     * at 0-14 dBA, pinned to the zero clamp. That page had been arguing against
     * its own anchor since it shipped.
     *
     * Still device-dependent by roughly +-15 dB, so any tool that shows an
     * absolute number must offer calibration and must not imply better accuracy
     * than that until the user has calibrated.
     *
     * NOT used by /snoring-monitor, which deliberately keeps its own +90 on an
     * unweighted low-band detector: its event thresholds are tuned to that scale
     * and it reports a night-long trend, not an absolute level. Unifying it
     * would be a false unification, not a fix.
     */
    var SPL_OFFSET_DEFAULT = 120;

    var TAU = { fast: 0.125, slow: 1.0, impulse: 0.035 };

    /**
     * IEC 61672-1 exponential detector over the squared signal:
     *   y[n] = y[n-1] + (1 - e^(-dt/tau)) * (x^2[n] - y[n-1])
     * dt comes from the caller, so a dropped animation frame cannot silently
     * change the effective time constant.
     */
    function createDetector(mode) {
        var tau = TAU[mode] || TAU.fast;
        var acc = 0, primed = false;
        return {
            // Changing Fast <-> Slow keeps the running value: the reading must
            // re-settle, not restart from zero.
            setMode: function (m) { tau = TAU[m] || TAU.fast; },
            push: function (meanSquare, dt) {
                if (!(meanSquare >= 0)) return;
                if (!primed) { acc = meanSquare; primed = true; return; }
                if (!(dt > 0)) dt = 1 / 60;
                if (dt > 1) dt = 1;                       // backgrounded tab: do not jump
                acc += (1 - Math.exp(-dt / tau)) * (meanSquare - acc);
            },
            value: function () { return acc; },
            reset: function () { acc = 0; primed = false; },
            tau: function () { return tau; }
        };
    }

    /**
     * ── Is this frame a measurement at all? ───────────────────────────────
     * An AnalyserNode on a context that is not rendering keeps handing back the
     * LAST buffer it filled. Nothing throws, nothing is zero: the numbers look
     * exactly like a live reading. Measured on /sound-meter 2026-09-18 with a
     * fake microphone stepping 100 dBA -> 50 dBA while the context was
     * suspended: the page showed "100.0 dBA" for as long as anyone cared to
     * look, and kept integrating that frame into Leq, Avg, Min and Max.
     * Production, same day: the pause outlasted three seconds in 935 of ~5100
     * measuring sessions in 14 days (18 %) - app switches on phones, tab
     * switches on desktop - so roughly one visitor in five was shown a frozen
     * number as if it were the room.
     *
     * `ctx.state` is not the test: a wedged context can report `running`, and
     * iOS has `interrupted`. The audio clock is. If `currentTime` has not
     * moved since the previous look, no new audio has been rendered, so the
     * analyser buffer is old - whatever the reason.
     *
     * graceMs exists because rAF can fire twice inside one render quantum on a
     * loaded machine; a quarter of a second is far below anything a person can
     * read off a meter and far above any scheduling jitter.
     */
    function createLiveClock(ctx, graceMs) {
        var grace = typeof graceMs === 'number' ? graceMs : 250;
        var lastCt = -1, movedAt = 0;
        return {
            live: function (nowMs) {
                var ct = ctx && typeof ctx.currentTime === 'number' ? ctx.currentTime : NaN;
                if (ct === ct && ct !== lastCt) { lastCt = ct; movedAt = nowMs; return true; }
                return movedAt > 0 && (nowMs - movedAt) < grace;
            }
        };
    }

    /**
     * ── Calibration: per microphone, shared by every level tool ───────────
     * Comment #1705 on /sound-meter (2026-09-18, Windows 10, Yandex Browser):
     * "completely inaccurate, it shows more than there really is". He was
     * right, and the cause is outside the page.
     *
     * Measured the same day on a Windows 11 laptop (Intel Smart Sound mic
     * array), three browsers capturing the SAME microphone at the SAME moment
     * with all processing constraints off: Chrome 70.8, Yandex 70.8, Edge
     * 70.9 dBA - the browser is not a factor. Then the Windows input level
     * (IAudioEndpointVolume; this driver's range is -96..+30 dB and the slider
     * sat at 100 % = +30 dB) was stepped with nothing else changing:
     *
     *     endpoint gain   +30    +20    +10     0  dB
     *     same room reads 71.3   61.0   51.3   43.1 dBA
     *
     * One to one. SPL_OFFSET_DEFAULT describes the microphone capsule; the OS
     * then adds a gain of its own that no web API exposes, and it is a gain,
     * so the error is one-sided - which is why nobody ever writes in to say
     * the meter reads LOW. In the /hearing-test ambient telemetry (329 people,
     * 30 days, people who went looking for a quiet room) iOS reads p50 34-37
     * dBA and Windows p50 48, p75 57, p90 64.
     *
     * A per-platform constant was considered and is excluded by that same
     * data: devices WITHOUT extra gain sit on the same noise-floor cluster on
     * every platform (25-30 dBA), so shifting "Windows" would break the ones
     * that are right and still leave a +30 dB machine 20 dB out. The error
     * belongs to a device, so the correction has to be measured on the device.
     *
     * Two ways to do that, both ending in the same stored offset:
     *   reference - the user types what another meter shows (a phone app
     *               will do). The accurate one.
     *   quiet     - the user says "it is quiet here" and we read the room.
     *               The one everybody can do. A room a person calls quiet is
     *               30-40 dBA, and what a consumer microphone reports there
     *               is that plus its own self-noise (~30 dBA), so the anchor
     *               is QUIET_ROOM_DBA = 35, the median of the iOS readings
     *               above. It is good to about +-7 dB and the UI says so; it
     *               replaces an error that was measured at +28.
     *
     * Keyed by microphone label, because the gain belongs to the input: a
     * headset and a laptop array on one machine differ by tens of dB. Stored
     * once, read by /sound-meter, /projector-noise-meter and /hearing-test -
     * three tools showing three different numbers for one room was the 08-09
     * incident, and a calibration only one of them honoured would be the same
     * bug again.
     */
    var QUIET_ROOM_DBA = 35;
    var CAL_WINDOW_DB = 40;                 // a stored offset further than this from the anchor is a mistake, not a microphone
    var CAL_KEY = 'aw_mic_cal_v1';
    var CAL_LEGACY_KEY = 'sm_cal_offset';   // /sound-meter's single global offset, pre 2026-09-18

    function calRead() {
        try { var m = JSON.parse(localStorage.getItem(CAL_KEY) || '{}'); return (m && typeof m === 'object') ? m : {}; }
        catch (_) { return {}; }
    }
    function calWrite(m) { try { localStorage.setItem(CAL_KEY, JSON.stringify(m)); return true; } catch (_) { return false; } }
    function calValid(o) { return typeof o === 'number' && isFinite(o) && o >= SPL_OFFSET_DEFAULT - CAL_WINDOW_DB && o <= SPL_OFFSET_DEFAULT + CAL_WINDOW_DB; }

    var calibration = {
        QUIET_ROOM_DBA: QUIET_ROOM_DBA,
        WINDOW_DB: CAL_WINDOW_DB,
        /* "Default - Microphone Array (...)" and its localised twin are one
           device; the alias prefix is written by the browser in the UI
           language, so it is cut at the first " - " only when the track says
           it IS an alias. */
        micKey: function (track) {
            var label = '', id = '';
            try { label = (track && track.label) || ''; } catch (_) {}
            try { id = (track && track.getSettings && track.getSettings().deviceId) || ''; } catch (_) {}
            if ((id === 'default' || id === 'communications') && label.indexOf(' - ') > 0) label = label.slice(label.indexOf(' - ') + 3);
            label = label.replace(/\s+/g, ' ').trim().toLowerCase();
            return label || '*';
        },
        get: function (micKey) {
            var m = calRead(), k = micKey || '*', e = m[k];
            if (e && calValid(e.o)) return { offset: e.o, method: e.m === 'quiet' ? 'quiet' : 'reference', at: e.t || 0 };
            if (e) { delete m[k]; calWrite(m); }
            /* the old global offset still applies until this microphone gets its own */
            try {
                var v = parseFloat(localStorage.getItem(CAL_LEGACY_KEY));
                if (calValid(v)) return { offset: v, method: 'reference', at: 0, legacy: true };
                if (isFinite(v)) localStorage.removeItem(CAL_LEGACY_KEY);
            } catch (_) {}
            return null;
        },
        set: function (micKey, offset, method) {
            if (!calValid(offset)) return false;
            var m = calRead();
            m[micKey || '*'] = { o: Math.round(offset * 100) / 100, m: method === 'quiet' ? 'quiet' : 'reference', t: Date.now() };
            return calWrite(m);
        },
        clear: function (micKey) {
            var m = calRead(); delete m[micKey || '*']; calWrite(m);
            try { localStorage.removeItem(CAL_LEGACY_KEY); } catch (_) {}
        },
        offsetFor: function (micKey) { var c = calibration.get(micKey); return c ? c.offset : SPL_OFFSET_DEFAULT; },
        /* Both take the mean square of the lane the reference is expressed in:
           A-weighted for `quiet` (QUIET_ROOM_DBA is a dBA figure), the
           displayed lane for `reference`. null = outside the physical window. */
        fromQuiet: function (meanSquareA) {
            if (!(meanSquareA > 1e-20)) return null;
            var o = QUIET_ROOM_DBA - 10 * Math.log10(meanSquareA);
            return calValid(o) ? o : null;
        },
        fromReference: function (meanSquare, referenceDb) {
            if (!(meanSquare > 1e-20) || !(referenceDb >= 0 && referenceDb <= 150)) return null;
            var o = referenceDb - 10 * Math.log10(meanSquare);
            return calValid(o) ? o : null;
        }
    };

    /**
     * Time-weighted level histogram -> L90, the acoustician's "background
     * level" (exceeded 90 % of the time). This is the number that tells a
     * gained-up input from a quiet room: speech and door slams move Leq and
     * Max, only the input's own floor moves L90. 1 dB bins, 0..150.
     */
    function createLevelStats() {
        var bins = new Float64Array(151), total = 0;
        return {
            push: function (db, dt) { if (!(db > 0) || !(dt > 0)) return; var b = db > 150 ? 150 : Math.round(db); bins[b] += dt; total += dt; },
            seconds: function () { return total; },
            /* the level exceeded `pct` % of the time */
            exceeded: function (pct) {
                if (!(total > 0)) return null;
                var need = total * (1 - pct / 100), acc = 0;
                for (var i = 0; i <= 150; i++) { acc += bins[i]; if (acc >= need) return i; }
                return 150;
            },
            reset: function () { for (var i = 0; i <= 150; i++) bins[i] = 0; total = 0; }
        };
    }

    window.AcousticWeighting = {
        design: design,
        createNode: createNode,
        createDetector: createDetector,
        createLiveClock: createLiveClock,
        createLevelStats: createLevelStats,
        calibration: calibration,
        qDb: qDb,
        TAU: TAU,
        SPL_OFFSET_DEFAULT: SPL_OFFSET_DEFAULT,
        // dB SPL from a mean square, with the shared anchor as the default.
        dbSpl: function (meanSquare, offset) {
            var o = typeof offset === 'number' ? offset : SPL_OFFSET_DEFAULT;
            return (meanSquare > 1e-20 ? 10 * Math.log10(meanSquare) : -200) + o;
        },
        POLE_FREQS: { f1: F1, f2: F2, f3: F3, f4: F4 },
        version: '1.1.0'
    };
})();
